import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';
import {
    MAPS_ADMIN_DASHBOARD,
    MAPS_ADMIN_EMAIL_ACTIVITY,
    MAPS_ADMIN_EMAIL_MESSAGES,
    MAPS_ADMIN_EMAIL_MESSAGE_DETAIL,
    MAPS_ADMIN_EMAIL_TIMELINE,
    MAPS_ADMIN_EMAIL_EVENTS_EXPORT,
} from './maps-graphql/queries';
import type {
    MapsAdminCompanyRow,
    MapsEmailActivityFilter,
    MapsEmailActivityGroupBy,
    MapsEmailActivityRow,
    MapsEmailEventsExportInput,
    MapsEmailMessageDetail,
    MapsEmailMessagePage,
    MapsEmailTimelinePoint,
    MapsExportResult,
} from '../models/maps.types';

/** Thin wrapper around the admin-gated dashboard + Email Activity operations (TRD §16). */
@Injectable({ providedIn: 'root' })
export class AdminService {
    constructor(private readonly apollo: Apollo) {}

    async Dashboard(): Promise<MapsAdminCompanyRow[]> {
        const result = await firstValueFrom(
            this.apollo.query<{ mapsAdminDashboard: MapsAdminCompanyRow[] }>({
                query: MAPS_ADMIN_DASHBOARD,
                fetchPolicy: 'network-only',
            })
        );
        return result.data?.mapsAdminDashboard ?? [];
    }

    /** Email activity aggregates at the requested grain — the drilldown table's data. */
    async EmailActivity(filter: MapsEmailActivityFilter | null, groupBy: MapsEmailActivityGroupBy): Promise<MapsEmailActivityRow[]> {
        const result = await firstValueFrom(
            this.apollo.query<{ mapsAdminEmailActivity: MapsEmailActivityRow[] }>({
                query: MAPS_ADMIN_EMAIL_ACTIVITY,
                variables: { filter, groupBy },
                fetchPolicy: 'network-only',
            })
        );
        return result.data?.mapsAdminEmailActivity ?? [];
    }

    /** Filtered cross-company message page, newest first. */
    async EmailMessages(filter: MapsEmailActivityFilter | null, limit: number, offset: number): Promise<MapsEmailMessagePage> {
        const result = await firstValueFrom(
            this.apollo.query<{ mapsAdminEmailMessages: MapsEmailMessagePage }>({
                query: MAPS_ADMIN_EMAIL_MESSAGES,
                variables: { filter, limit, offset },
                fetchPolicy: 'network-only',
            })
        );
        return result.data?.mapsAdminEmailMessages ?? { items: [], totalCount: 0 };
    }

    /** One message with full content, sender provenance, and its raw event timeline. */
    async EmailMessageDetail(id: string): Promise<MapsEmailMessageDetail | null> {
        const result = await firstValueFrom(
            this.apollo.query<{ mapsAdminEmailMessageDetail: MapsEmailMessageDetail }>({
                query: MAPS_ADMIN_EMAIL_MESSAGE_DETAIL,
                variables: { id },
                fetchPolicy: 'network-only',
            })
        );
        return result.data?.mapsAdminEmailMessageDetail ?? null;
    }

    /** Delivered/open/click event counts over time for the filtered messages, zero-filled. */
    async EmailTimeline(filter: MapsEmailActivityFilter | null): Promise<MapsEmailTimelinePoint[]> {
        const result = await firstValueFrom(
            this.apollo.query<{ mapsAdminEmailTimeline: MapsEmailTimelinePoint[] }>({
                query: MAPS_ADMIN_EMAIL_TIMELINE,
                variables: { filter },
                fetchPolicy: 'network-only',
            })
        );
        return result.data?.mapsAdminEmailTimeline ?? [];
    }

    /** Build + upload the filtered event export; returns the pre-auth download URL. */
    async EmailEventsExport(input: MapsEmailEventsExportInput): Promise<MapsExportResult> {
        const result = await firstValueFrom(
            this.apollo.mutate<{ mapsAdminEmailEventsExport: MapsExportResult }>({
                mutation: MAPS_ADMIN_EMAIL_EVENTS_EXPORT,
                variables: { input },
            })
        );
        const payload = result.data?.mapsAdminEmailEventsExport;
        if (!payload) {
            throw new Error('Export did not return a download URL');
        }
        return payload;
    }
}
